import { useQuery } from "@tanstack/react-query";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { endpointsMetadata } from "../routes/endpointsMetadata";
import { IUserModel } from "./useAuthentication";
import axiosInstance, { setAxiosToken } from "../helpers/axiosHelper";
import { keysToCamelCase } from "../utilities/apiUtility";

interface IApiResponse<T = any> {
  data: T;
  status: number;
  message?: string;
}

/**
 * Hook to fetch fresh user profile data from the server.
 * This ensures that user data like isUnreliable and permissions are up-to-date,
 * even if the JWT token hasn't expired yet (7 day expiration).
 *
 * NOTE: This hook uses useQuery directly instead of useApiQuery to avoid
 * circular dependency (useApiQuery -> useAuthentication -> useFreshUserProfile -> useApiQuery)
 */
export const useFreshUserProfile = (enabled: boolean) => {
  const currentUser = useAuthUser() as IUserModel;

  const { data, isLoading, refetch } = useQuery<Partial<IUserModel>>({
    queryKey: [endpointsMetadata.authentication.getUserProfile],
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - consider data stale after 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache for 10 minutes
    retry: true,
    queryFn: async () => {
      setAxiosToken(currentUser?.authToken);

      try {
        const res = await axiosInstance.get<IApiResponse<Partial<IUserModel>>>(
          endpointsMetadata.authentication.getUserProfile
        );
        const apiResponse = keysToCamelCase(res.data);

        if (res.status >= 200 && res.status < 300) {
          return apiResponse as Partial<IUserModel>;
        } else {
          throw new Error(apiResponse?.message || "Server error.");
        }
      } catch (error) {
        // Silently fail - don't show error toast for failed profile refresh
        throw error;
      }
    },
  });

  return {
    freshUserData: data,
    isLoadingFreshData: isLoading,
    refreshUserProfile: refetch,
  };
};
